/* eslint-disable no-restricted-syntax */
import moment from "moment";

import { getStatementFareLabel, getTransactionFlightDate } from "../../utils/statementFare";

const formatName = (name: string) => {
  if (name.includes("/")) {
    const firstName = name.split("/")[1].toLowerCase();
    const lastName = name.split("/")[0].toLowerCase();
    const fullName = firstName.concat(" ", lastName);
    const words = fullName.split(" ");
    const capitalizedWords = words.map((word) => word[0].toUpperCase() + word.substring(1));
    return capitalizedWords.join(" ");
  }
  return name;
};
const LOY_TRANSACTION_REDEMPTION_ITEM = "LOY Transaction Redemption Item";
const LOY_TRANSACTION_ACCRUAL_ITEM = "LOY Transaction Accrual Item";
const POINT_TYPE = "Point Type";
const PRODUCT_NAME = "Product Name";
const TRANSACTION_TYPE = "Transaction Type";
const momentObj = (transaction: Transaction) => moment(transaction["Process Date"], "MM/DD/YYYY hh:mm:ss");
const formatDate = (date: string) => moment(date, "MM/DD/YYYY hh:mm:ss").format("DD/MM/YYYY");

const getQualifiedPoints = (transaction: Transaction): number => {
  let qualifiedPoints = 0;
  if (transaction.isParentTransaction) {
    const transactions = transaction.children;
    transactions.forEach((item) => {
      if (item[LOY_TRANSACTION_ACCRUAL_ITEM]) {
        item[LOY_TRANSACTION_ACCRUAL_ITEM].forEach((accrual) => {
          if (accrual[POINT_TYPE] && accrual[POINT_TYPE].toUpperCase().includes("QUALIFICÁVEIS")) {
            qualifiedPoints += Number(accrual["Accrualed Value"]);
          }
        });
      }
    });
  } else if (transaction[LOY_TRANSACTION_REDEMPTION_ITEM]) {
    const redemptions = transaction[LOY_TRANSACTION_REDEMPTION_ITEM];
    redemptions.forEach((item) => {
      if (item[POINT_TYPE] && item[POINT_TYPE].toUpperCase().includes("QUALIFICÁVEIS")) {
        qualifiedPoints -= Number(item.Value);
      }
    });
  }
  return qualifiedPoints;
};
const isBonus = (transaction: Transaction) => {
  let hasBonus = false;
  if (transaction[LOY_TRANSACTION_ACCRUAL_ITEM]) {
    transaction[LOY_TRANSACTION_ACCRUAL_ITEM].forEach((el) => {
      const email = el["GOL Member Email Type"];
      const pointSubType = el["Point Sub Type"];
      hasBonus =
        (email && email.toUpperCase() === "BONUS") ||
        email.toUpperCase() === "BÔNUS" ||
        pointSubType === "Bonus Smiles";
      return hasBonus;
    });
  }
  return hasBonus;
};
const getResampledMiles = (transaction: Transaction): number => {
  let sum = 0;
  const redemptionItem = transaction[LOY_TRANSACTION_REDEMPTION_ITEM];
  if (redemptionItem && !isBonus(transaction)) {
    sum = redemptionItem.reduce((acc, curr) => {
      if (curr[POINT_TYPE] !== "Milhas Club" && !curr[POINT_TYPE].toUpperCase().includes("SEGMENTOS")) {
        return acc + Number(curr.Value);
      }
      return acc;
    }, 0);
  } else if (!isBonus(transaction)) {
    sum += Number(transaction.Amount);
  }
  return sum;
};
const getAccrualedMiles = (transaction: Transaction): number => {
  let sum = 0;
  const accrualItem = transaction[LOY_TRANSACTION_ACCRUAL_ITEM];
  if (accrualItem) {
    sum = accrualItem.reduce((acc, curr) => {
      if (curr["Qualifying Flag"] === "N") {
        return acc + Number(curr["Accrualed Value"]);
      }
      return acc;
    }, 0);
  }
  return sum;
};
const getVoebizPoints = (transaction: Transaction): number => {
  let miles = 0;
  if (transaction.isParentTransaction) {
    miles = transaction.children.reduce((acc, curr) => {
      if (curr[TRANSACTION_TYPE] === "Redemption") {
        return acc + getResampledMiles(curr) * -1;
      }
      return acc + getAccrualedMiles(curr);
    }, 0);
  } else if (transaction[TRANSACTION_TYPE] === "Redemption") {
    miles = getResampledMiles(transaction) * -1;
  } else {
    miles = getAccrualedMiles(transaction);
  }
  return miles;
};
const getTransactionDate = (transaction: Transaction): string => formatDate(transaction["Transaction Date"]);
const getTraveler = (transaction: Transaction): string => {
  const flightPaxName = "Flight PAX Name";
  if (transaction.isParentTransaction) {
    return formatName(transaction.children[0][flightPaxName]);
  }
  if (transaction[flightPaxName]) {
    return formatName(transaction[flightPaxName]);
  }
  return "";
};
const getExpireDate = (transaction: Transaction): string => {
  const accrualItems = transaction[LOY_TRANSACTION_ACCRUAL_ITEM];
  if (transaction[TRANSACTION_TYPE] === "Redemption" || (accrualItems?.length === 1 && isBonus(transaction))) {
    return "";
  }
  const expireDate = (t: Transaction) => {
    let validity = "";
    const accrualItem = t[LOY_TRANSACTION_ACCRUAL_ITEM];
    if (accrualItem) {
      accrualItem.forEach((el) => {
        const email = el["GOL Member Email Type"].toUpperCase();
        if (el["Qualifying Flag"] === "N" && email !== "BONUS" && email !== "BÔNUS") {
          validity = formatDate(el["Expiration Date"]);
        }
      });
    }
    return validity;
  };
  if (transaction.isParentTransaction && transaction.children.length > 0) {
    return expireDate(transaction.children[0]);
  }
  return expireDate(transaction);
};

const TransactionsMapper = (response: ReadonlyArray<unknown>, golPremiumEnabled = false): TransactionInfo[] => {
  const getDate = (transaction: Transaction): string => momentObj(transaction).format("DD/MM/YYYY");
  const getOldest = (a: Transaction, b: Transaction) => momentObj(a).diff(momentObj(b));
  const getName = (transaction: Transaction): string => {
    if (transaction.isParentTransaction) {
      const alias = ["G3", "G3S", "SV3"];
      const oldest = [...transaction.children].sort(getOldest)[0];
      if (oldest["Transaction Sub Type"] === "Cancellation") {
        return "Cancelamento";
      }
      if (alias.includes(oldest["Partner Alias"])) {
        return oldest["Operating Carrier"] || oldest["Partner Name"];
      }
      return oldest["External Reservation Number"];
    }
    return getStatementFareLabel(
      transaction[PRODUCT_NAME],
      getTransactionFlightDate(transaction),
      golPremiumEnabled,
    ) as string;
  };
  const getDescription = (transaction: Transaction): string => {
    if (transaction.isParentTransaction) {
      const child = transaction.children[0];
      return getStatementFareLabel(child[PRODUCT_NAME], getTransactionFlightDate(child), golPremiumEnabled) as string;
    }
    return getStatementFareLabel(
      transaction[PRODUCT_NAME],
      getTransactionFlightDate(transaction),
      golPremiumEnabled,
    ) as string;
  };
  const getLocator = (transaction: Transaction): string => {
    if (transaction.isParentTransaction) {
      return transaction.children[0]["Flight PNR"];
    }
    return transaction["Flight PNR"];
  };
  const getId = (transaction: Transaction): string => {
    if (transaction.isParentTransaction) {
      return transaction.children[0]["Row Id"];
    }
    return transaction["Row Id"];
  };
  const getStatus = (transaction: Transaction): string => {
    let status;
    let subStatus;
    if (transaction.isParentTransaction) {
      status = transaction.children[0].Status;
      subStatus = transaction.children[0]["Sub Status"];
    } else {
      status = transaction.Status;
      subStatus = transaction["Sub Status"];
    }
    if (status === "Processed" && subStatus === "Success") {
      return "Processado";
    }
    if (status === "Cancelled" && subStatus === "Success") {
      return "Cancelado";
    }
    if (status === "Acceptable") {
      return "Aceito";
    }
    return "Erro";
  };
  const getVoebizPointsChildren = (children: Transaction) => {
    let miles = 0;
    if (children[TRANSACTION_TYPE] === "Redemption") {
      miles -= getResampledMiles(children);
    } else {
      miles += getAccrualedMiles(children);
    }
    return miles;
  };
  const getTravelerChildren = (children: Transaction): string => {
    const flightPaxName = "Flight PAX Name";
    return formatName(children[flightPaxName]);
  };
  const getDescriptionChildren = (children: Transaction): string => {
    return getStatementFareLabel(
      children[PRODUCT_NAME],
      getTransactionFlightDate(children),
      golPremiumEnabled,
    ) as string;
  };
  const getLocatorChildren = (children: Transaction): string => {
    return children["Flight PNR"];
  };
  const getIdChildren = (children: Transaction): string => {
    return children["Row Id"];
  };
  const getStatusChildren = (children: Transaction): string => {
    const status = children.Status;
    const subStatus = children["Sub Status"];
    if (status === "Processed" && subStatus === "Success") {
      return "Processado";
    }
    if (status === "Cancelled" && subStatus === "Success") {
      return "Cancelado";
    }
    if (status === "Acceptable") {
      return "Aceito";
    }
    return "Erro";
  };
  const getTransactionsDetails = (transaction: Transaction): TransactionsDetails[] => {
    const transactionsDetails: TransactionsDetails[] = [];
    if (transaction.isParentTransaction) {
      transaction.children.map((children) => {
        const details = {
          locator: getLocatorChildren(children),
          voebizPoints: getVoebizPointsChildren(children), //
          description: getDescriptionChildren(children),
          traveler: getTravelerChildren(children),
          id: getIdChildren(children),
          status: getStatusChildren(children),
        };
        transactionsDetails.push(details);
        return undefined;
      });
      return transactionsDetails;
    }
    const details = {
      locator: getLocator(transaction),
      voebizPoints: getVoebizPoints(transaction),
      description: getDescription(transaction),
      traveler: getTraveler(transaction),
      id: getId(transaction),
      status: getStatus(transaction),
    };
    transactionsDetails.push(details);
    return transactionsDetails;
  };
  const getIsLegacy = (transaction: Transaction): boolean => {
    if (transaction.legacy) {
      return true;
    }
    return false;
  };
  return (response as Transaction[]).map((transaction) => ({
    date: getDate(transaction),
    name: getName(transaction),
    qualifiedPoints: getQualifiedPoints(transaction),
    transactionDate: getTransactionDate(transaction),
    expireDate: getExpireDate(transaction),
    company: "GOL Linhas Aéreas",
    locator: getLocator(transaction),
    voebizPoints: getVoebizPoints(transaction),
    description: getDescription(transaction),
    traveler: getTraveler(transaction),
    id: getId(transaction),
    status: getStatus(transaction),
    transactionsDetails: getTransactionsDetails(transaction),
    isLegacy: getIsLegacy(transaction),
  }));
};
export default TransactionsMapper;
